/**
 * HelpSurfaceContent — the shared inner card for a contextual help entry (WO-H1).
 * Identical on native and web; only the surrounding container (bottom sheet vs popover)
 * differs per platform. Theme tokens only; copper is NOT used here (reserved for the
 * single owner-authority action per screen, invariant 6).
 */
import React from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import type { HelpEntry } from '@beam/api-types';
import { Text } from '../ui';
import { theme } from '../../theme';
import { HelpBody } from './HelpBody';

export interface HelpSurfaceProps {
  visible: boolean;
  entry: HelpEntry | undefined;
  relatedEntries?: HelpEntry[];
  onClose: () => void;
  onOpenRelated?: (entry: HelpEntry) => void;
  /** Optional: route to the full Help Center. */
  onOpenHelpCenter?: () => void;
}

export function HelpSurfaceContent({
  entry,
  relatedEntries = [],
  onClose,
  onOpenRelated,
  onOpenHelpCenter,
}: Omit<HelpSurfaceProps, 'visible'>) {
  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <Text variant="label" color="secondary">
          HELP
        </Text>
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close help"
          hitSlop={12}
          style={styles.close}
        >
          <Text variant="subhead" color="secondary">
            {'×'}
          </Text>
        </Pressable>
      </View>

      {entry ? (
        <>
          <Text variant="title">{entry.title}</Text>
          <HelpBody body={entry.body} />

          {relatedEntries.length > 0 && (
            <View style={styles.related}>
              <Text variant="label" color="secondary">
                RELATED
              </Text>
              {relatedEntries.map((r) => (
                <Pressable
                  key={r.id}
                  onPress={() => onOpenRelated?.(r)}
                  accessibilityRole="button"
                  accessibilityLabel={`Open help: ${r.title}`}
                  style={styles.relatedRow}
                >
                  <Text variant="body" color="link">
                    {r.title}
                  </Text>
                </Pressable>
              ))}
            </View>
          )}
        </>
      ) : (
        <>
          <Text variant="title">Help</Text>
          <Text variant="body" color="secondary">
            There’s no specific help for this screen yet. Open the Help Center for step-by-step guides.
          </Text>
        </>
      )}

      {onOpenHelpCenter && (
        <Pressable
          onPress={onOpenHelpCenter}
          accessibilityRole="button"
          accessibilityLabel="Open the Help Center"
          style={styles.centerLink}
        >
          <Text variant="bodyStrong" color="link">
            Open the Help Center →
          </Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: theme.color.bg.surface,
    borderTopLeftRadius: theme.radius.lg,
    borderTopRightRadius: theme.radius.lg,
    borderRadius: theme.radius.lg,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    padding: theme.spacing.xl,
    gap: theme.spacing.md,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  close: { minWidth: theme.layout.minTapTarget, minHeight: 28, alignItems: 'flex-end', justifyContent: 'center' },
  related: { gap: theme.spacing.xs, marginTop: theme.spacing.sm },
  relatedRow: { minHeight: theme.layout.minTapTarget, justifyContent: 'center' },
  centerLink: { minHeight: theme.layout.minTapTarget, justifyContent: 'center', marginTop: theme.spacing.xs },
});
